import React, { useContext, useEffect, useRef, useState } from 'react';

import "./RainforestForm.scss";
import {
    RainforestApproveEvent,
    RainforestFormElement,
    RainforestPayinResponse,
    RainforestSessionResponse,
} from "../../../../context/RainforestContext";
import {ToastService} from "../../../../service/ToastService";
import {getRainforestPayinConfig, getRainforestSession} from "../../../../api/RainforestApi";
import {PreloaderContext} from "../../../../context/PreloaderContext";
import {PAYMENT_METHOD_TYPE} from "../AchSwitcher/AchSwitcher";
import {RainforestPaymentMethodEnum} from "../../../../enums/rainforest-payment-method.enum";
import {
    RainforestCardBrandEnum,
    RainforestCardBrandUpdatedDetail,
} from "../../../../enums/rainforest-card-brand.enum";

interface RainforestFormProps {
    amount: string;
    paymentMethod: PAYMENT_METHOD_TYPE;
    onToggleValid: (isValid: boolean) => void;
    setRainforestForm: (rainforestForm: RainforestFormElement) => void;
    onChangePaymentMethod: (newPaymentMethod: PAYMENT_METHOD_TYPE) => void;
    onChangeLoadingState: (isLoading: boolean) => void;
    isAchAvailable: boolean;
    isCreditCardAvailable: boolean;
    onApprovedPayment: (paymentMethod: RainforestPaymentMethodEnum, payinId: string, payinConfigId: string) => void;
    onDeclinedPayment: () => void;
}

export const RainforestForm: React.FunctionComponent<RainforestFormProps> = (props) => {
    const rainforestFormWrapperRef = useRef<HTMLDivElement | null>(null);
    const rainforestFormRef = useRef<HTMLElement | null>(null);
    const preloaderContext = useContext(PreloaderContext);

    // Ref that always reflects the latest props — read by long-lived event listeners
    // that were registered once during initRainforest.
    const propsRef = useRef(props);
    propsRef.current = props;

    const [currentCardBrand, setCurrentCardBrand] = useState<RainforestCardBrandEnum | null>(null);

    // Initialize Rainforest form on mount
    useEffect(() => {
        initRainforest();
    }, []);

    // Update Payin Config ID when amount changes, payment method, or card branch changes
    useEffect(() => {
        const isMounted: boolean = rainforestFormRef.current !== null;
        if (!isMounted) {
            return;
        }

        const abortController = new AbortController();

        props.onChangeLoadingState(true);
        getRainforestPayinConfigId(props.amount, currentCardBrand, abortController.signal)
            .then(updateRainforestPayinConfigId)
            .catch((error) => {
                if (error.name === 'AbortError') {
                    // Request was aborted, no action needed
                } else {
                    // In case of error, reset card brand state to trigger a new config fetch
                    if (currentCardBrand) {
                        setCurrentCardBrand(null);
                    }
                }
            })
            .finally(() => {
                props.onChangeLoadingState(false);
            })

        return () => {
            abortController.abort();
        };
    }, [props.amount, props.paymentMethod, currentCardBrand]);

    const getRainforestPayinConfigId = async (amount: string, currentCardBrand?: RainforestCardBrandEnum | null, abortSignal?: AbortSignal): Promise<string> => {
        const isPaymentMethodAch: boolean = props.paymentMethod === PAYMENT_METHOD_TYPE.ACH;
        const effectiveCardBrand: RainforestCardBrandEnum | null = isPaymentMethodAch ? null : (currentCardBrand ?? null);
        return getRainforestPayinConfig(parseFloat(amount).toFixed(2), isPaymentMethodAch, effectiveCardBrand, abortSignal).then(({ payin_config_id }: RainforestPayinResponse) => payin_config_id);
    };

    const updateRainforestPayinConfigId = async (newPayinConfig: string) => {
        if (rainforestFormRef.current) {
            rainforestFormRef.current.setAttribute('payin-config-id', newPayinConfig);
        }
    };

    const initRainforest = async () => {
        preloaderContext.showMiddlePagePreloader();
        props.onChangeLoadingState(true);
        const rainforestSessionPromise = getRainforestSession();
        const rainforestPayinConfigPromise = getRainforestPayinConfigId(props.amount);
        Promise.all([rainforestSessionPromise, rainforestPayinConfigPromise])
            .then(([sessionResponse, payinConfigId]: [rainforestSessionResponse: RainforestSessionResponse, rainforestPayinResponse: string]) => {
                if (!rainforestFormWrapperRef.current) {
                    console.error('Rainforest form wrapper ref is null');
                    return;
                }

                // Filter Rainforest allowed methods based on what methods are enabled for this Public Link
                //
                //  We filter payment methods on Frontend side so far. Maybe we will move this logic to Backend in future
                //   when we will need to filter it in other places as well
                const rainforestAllowedMethodsFilteredByPublicLink: RainforestPaymentMethodEnum[] = sessionResponse.allowed_methods.filter((method: RainforestPaymentMethodEnum) => {
                    if (method === RainforestPaymentMethodEnum.Ach || method === RainforestPaymentMethodEnum.ValidatedAch) {
                        return props.isAchAvailable;
                    }
                    if (method === RainforestPaymentMethodEnum.Card || method === RainforestPaymentMethodEnum.ApplePay) {
                        return props.isCreditCardAvailable;
                    }
                    return true;
                });
                if (!rainforestAllowedMethodsFilteredByPublicLink.length) {
                    ToastService.middleScreenErrorToast('No payment methods are available');
                    return;
                }

                const rainforestFormElement = document.createElement('rainforest-payment');
                rainforestFormRef.current = rainforestFormElement;
                rainforestFormElement.setAttribute('session-key', sessionResponse.session_key);
                rainforestFormElement.setAttribute('hide-button', 'true');
                updateRainforestPayinConfigId(payinConfigId);
                rainforestFormElement.setAttribute('allowed-methods', rainforestAllowedMethodsFilteredByPublicLink.join(','));
                const initialPaymentMethodType: PAYMENT_METHOD_TYPE = rainforestAllowedMethodsFilteredByPublicLink[0] === RainforestPaymentMethodEnum.Card
                    ? PAYMENT_METHOD_TYPE.CREDIT_CARD
                    : PAYMENT_METHOD_TYPE.ACH;
                props.onChangePaymentMethod(initialPaymentMethodType);
                rainforestFormWrapperRef.current.appendChild(rainforestFormElement);
                rainforestFormElement.addEventListener('valid', () => {
                    props.onToggleValid(true);
                });
                rainforestFormElement.addEventListener('invalid', () => {
                    props.onToggleValid(false);
                });
                (rainforestFormElement as any).addEventListener('approved', (data: RainforestApproveEvent) => {
                    props.onApprovedPayment(data.detail[0].data.method_type, data.detail[0].data.payin_id as string, data.detail[0].data.payin_config_id as string);
                });
                rainforestFormElement.addEventListener('declined', (data: any) => {
                    ToastService.middleScreenErrorToast('Please try again or contact FieldPulse support.');
                    props.onDeclinedPayment()
                });
                rainforestFormElement.addEventListener('method-updated', (event: any) => {
                    if (event.detail.includes(RainforestPaymentMethodEnum.Ach) || event.detail.includes(RainforestPaymentMethodEnum.ValidatedAch)) {
                        props.onChangePaymentMethod(PAYMENT_METHOD_TYPE.ACH);
                    } else if (event.detail.includes(RainforestPaymentMethodEnum.Card)) {
                        props.onChangePaymentMethod(PAYMENT_METHOD_TYPE.CREDIT_CARD);
                    }
                });
                rainforestFormElement.addEventListener('card-brand-updated', (event) => {
                    if (propsRef.current.paymentMethod === PAYMENT_METHOD_TYPE.ACH || !rainforestFormRef.current) {
                        return;
                    }

                    const detail = (event as CustomEvent<string[]>).detail;
                    const newCardBrandDetail = (detail?.[0] ?? '') as RainforestCardBrandUpdatedDetail;
                    const newCardBrand: RainforestCardBrandEnum | null = newCardBrandDetail && newCardBrandDetail !== 'NONE'
                        ? newCardBrandDetail
                        : null;

                    setCurrentCardBrand(newCardBrand);
                });
                rainforestFormElement.addEventListener('loaded', () => {
                    preloaderContext.hideMiddlePagePreloader();
                })
                props.setRainforestForm(rainforestFormElement as any as RainforestFormElement);
            })
            .finally(() => {
                props.onChangeLoadingState(false);
            });
    }

    return <div ref={rainforestFormWrapperRef}>
    </div>
}
